import { useMemo } from 'react';
import { formatDate, getTimeProgress } from '@/utils/dateUtils';

interface ProgressTimelineProps {
  startDate: string | Date | null | undefined;
  endDate: string | Date | null | undefined;
  creationDate?: string | Date | null | undefined;
  className?: string;
}

export function ProgressTimeline({
  startDate,
  endDate,
  creationDate,
  className = '',
}: ProgressTimelineProps) {
  const progress = useMemo(
    () => getTimeProgress(startDate, endDate),
    [startDate, endDate],
  );

  const barColor = progress.isExpired
    ? 'tw-bg-red-400'
    : progress.percent > 75
      ? 'tw-bg-yellow-400'
      : 'tw-bg-green-400';

  const labelColor = progress.isExpired
    ? 'tw-text-red-600'
    : 'tw-text-gray-600';

  return (
    <div className={`tw-space-y-2 ${className}`}>
      {/* Progress bar */}
      <div className="tw-flex tw-items-center tw-gap-3">
        {/* Start */}
        <div className="tw-flex tw-flex-col tw-items-center tw-flex-shrink-0">
          <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-green-500 tw-border-2 tw-border-white tw-shadow-sm" />
          <span className="tw-text-[10px] tw-text-gray-500 tw-mt-1">{formatDate(startDate)}</span>
        </div>

        {/* Bar */}
        <div className="tw-flex-1 tw-relative">
          <div className="tw-h-2 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden">
            <div
              className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 ${barColor}`}
              style={{ width: `${Math.min(100, progress.percent)}%` }}
            />
          </div>
          {/* Cursor */}
          {!progress.isExpired && progress.percent > 0 && (
            <div
              className="tw-absolute tw-top-1/2 tw--translate-y-1/2 tw-w-3.5 tw-h-3.5 tw-rounded-full tw-bg-white tw-border-2 tw-border-affinicar-primary tw-shadow-sm tw-transition-all tw-duration-500"
              style={{ left: `calc(${Math.min(100, progress.percent)}% - 7px)` }}
            />
          )}
        </div>

        {/* End */}
        <div className="tw-flex tw-flex-col tw-items-center tw-flex-shrink-0">
          <div className={`tw-w-3 tw-h-3 tw-rounded-full tw-border-2 tw-border-white tw-shadow-sm ${
            progress.isExpired ? 'tw-bg-red-400' : 'tw-bg-gray-300'
          }`} />
          <span className="tw-text-[10px] tw-text-gray-500 tw-mt-1">{formatDate(endDate)}</span>
        </div>
      </div>

      {/* Label */}
      <div className="tw-flex tw-items-center tw-justify-between">
        <span className={`tw-text-xs tw-font-medium ${labelColor}`}>
          {progress.label}
        </span>
        {creationDate && (
          <span className="tw-text-[10px] tw-text-gray-400">
            Créé le {formatDate(creationDate)}
          </span>
        )}
      </div>
    </div>
  );
}
